import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ZoomIn } from 'lucide-react';

/**
 * ZoomableImage — renders an image that opens a full-screen zoom modal on click.
 * Closes on backdrop click, the X button, or the Escape key.
 */
export const ZoomableImage = ({ src, alt, className = '', containerClassName = '', testId }) => {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [open]);

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className={`group relative block w-full cursor-zoom-in ${containerClassName}`}
        aria-label={`Zoom image: ${alt}`}
        data-testid={testId}
      >
        <img src={src} alt={alt} loading="lazy" className={className} />
        <span className="absolute top-2 right-2 w-8 h-8 rounded-full bg-black/60 border border-white/20 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
          <ZoomIn size={16} className="text-[#00FFD1]" />
        </span>
      </button>

      {createPortal(
        <AnimatePresence>
          {open && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="fixed inset-0 bg-black/90 backdrop-blur-sm flex items-center justify-center p-4 sm:p-10"
              style={{ zIndex: 100000 }}
              onClick={() => setOpen(false)}
              data-testid={testId ? `${testId}-modal` : 'zoom-modal'}
            >
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="absolute top-5 right-5 w-11 h-11 rounded-full bg-white/10 border border-white/20 flex items-center justify-center text-white hover:bg-white/20 transition-colors"
                aria-label="Close zoom"
                data-testid="zoom-close"
              >
                <X size={22} />
              </button>
              <motion.img
                initial={{ scale: 0.92 }}
                animate={{ scale: 1 }}
                exit={{ scale: 0.92 }}
                transition={{ duration: 0.2 }}
                src={src}
                alt={alt}
                onClick={(e) => e.stopPropagation()}
                className="max-w-full max-h-[88vh] object-contain rounded-lg shadow-2xl"
              />
              <p className="absolute bottom-5 left-1/2 -translate-x-1/2 text-white/60 text-xs font-mono px-3 py-1 bg-black/50 rounded-full max-w-[90vw] truncate">{alt}</p>
            </motion.div>
          )}
        </AnimatePresence>,
        document.body
      )}
    </>
  );
};

export default ZoomableImage;
